import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/whatsapp-web';

export type WhatsappWebStatus =
  | 'disconnected'
  | 'starting'
  | 'qr'
  | 'authenticated'
  | 'ready'
  | 'auth_failure'
  | 'error';

export interface WhatsappWebState {
  status: WhatsappWebStatus;
  /** Data URL of the QR image, present only while status === 'qr'. */
  qr: string | null;
  me: { number: string; pushname: string } | null;
  lastError: string;
  startedAt: string | null;
}

export interface WhatsappChat {
  id: string;
  name: string;
  isGroup: boolean;
  unreadCount: number;
  lastMessage: string;
  timestamp: number | null;
}

export interface WhatsappMessage {
  id: string;
  body: string;
  fromMe: boolean;
  author: string;
  timestamp: number | null;
  type?: string;
  hasMedia?: boolean;
  location?: { lat: number; lng: number; desc?: string } | null;
}

export interface WhatsappContact {
  id: string;
  name: string;
  pushname: string;
  number: string;
  picUrl: string | null;
  isGroup: boolean;
}

export const whatsappWebKeys = {
  root: ['whatsapp-web'] as const,
  status: ['whatsapp-web', 'status'] as const,
  chats: ['whatsapp-web', 'chats'] as const,
  messages: (chatId: string | null) => ['whatsapp-web', 'messages', chatId] as const,
  contact: (chatId: string | null) => ['whatsapp-web', 'contact', chatId] as const,
};

/** Polls quickly while linking (QR rotates), slowly once connected. */
export function useWhatsappWebStatus() {
  return useQuery({
    queryKey: whatsappWebKeys.status,
    queryFn: async () => (await api.get<WhatsappWebState>(`${BASE}/status`)).data,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'qr' || s === 'starting' || s === 'authenticated' ? 3_000 : 15_000;
    },
  });
}

export function useWhatsappChats(enabled: boolean) {
  return useQuery({
    queryKey: whatsappWebKeys.chats,
    queryFn: async () => (await api.get<{ chats: WhatsappChat[] }>(`${BASE}/chats`)).data.chats,
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useWhatsappMessages(chatId: string | null) {
  return useQuery({
    queryKey: whatsappWebKeys.messages(chatId),
    queryFn: async () =>
      (await api.get<{ messages: WhatsappMessage[] }>(`${BASE}/chats/${encodeURIComponent(chatId!)}/messages`)).data.messages,
    enabled: !!chatId,
    refetchInterval: chatId ? 8_000 : false,
  });
}

export function useWhatsappContact(chatId: string | null) {
  return useQuery({
    queryKey: whatsappWebKeys.contact(chatId),
    queryFn: async () =>
      (await api.get<{ contact: WhatsappContact | null }>(`${BASE}/chats/${encodeURIComponent(chatId!)}/contact`)).data.contact,
    enabled: !!chatId,
    staleTime: 60 * 60 * 1000,
  });
}

export function useWhatsappWebMutations() {
  const qc = useQueryClient();
  const refreshStatus = () => qc.invalidateQueries({ queryKey: whatsappWebKeys.status });

  const connect = useMutation({
    mutationFn: async (options?: { restart?: boolean }) =>
      (await api.post<WhatsappWebState>(`${BASE}/connect`, options ?? {})).data,
    onSuccess: refreshStatus,
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post<WhatsappWebState>(`${BASE}/disconnect`)).data,
    onSuccess: refreshStatus,
  });

  const send = useMutation({
    mutationFn: async (payload: { to: string; body: string }) =>
      (await api.post(`${BASE}/send`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: whatsappWebKeys.chats });
      qc.invalidateQueries({ queryKey: ['whatsapp-web', 'messages'] });
      qc.invalidateQueries({ queryKey: ['comm'] });
    },
  });

  const sendMedia = useMutation({
    mutationFn: async (payload: { to: string; body: string; data: string; mimetype: string; filename: string }) =>
      (await api.post(`${BASE}/send-media`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: whatsappWebKeys.chats });
      qc.invalidateQueries({ queryKey: ['whatsapp-web', 'messages'] });
      qc.invalidateQueries({ queryKey: ['comm'] });
    },
  });

  return { connect, disconnect, send, sendMedia };
}
